import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import type { CardAttributeQueryParams } from "src/app/features/CardImplementation/subFeatures/CardDefinition/subFeatures/CardAttribute/services/server/CardAttributesQueryTypes";
import { FormManager } from "../../../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../../../core/router/types/routerTypes";
import { useCardAttributeLoadQuery, useCardAttributeUpdateMutation } from "../../services/server/CardAttributeApi";
import type { CardAttributeEntity } from "../../types/CardAttributeEntity";

export const CardAttributeEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { cardImplementationId, cardDefinitionId, id } = useParams();
    const [queryConfig, setQueryConfig] = useState<CardAttributeQueryParams>({} as CardAttributeQueryParams);
    useEffect(() => {
        if (cardImplementationId && cardDefinitionId && id) {
            setQueryConfig({ cardImplementationId, cardDefinitionId, id });
        }
    }, [cardImplementationId, cardDefinitionId, id]);
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("card-attribute");

    const [formData, setFormData] = useState<CardAttributeEntity>({} as CardAttributeEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCardAttributeLoadQuery({
        cardImplementationId,
        cardDefinitionId,
        id,
    });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardAttributeUpdateMutation();
    const completeSubmit = useFormSubmitCompletion(
        formAction,
        `${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${FORM_ACTION.EDIT}/${cardDefinitionId}`,
    );
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData || !queryConfig) {
                return;
            }

            const result = await saveFormData({
                cardAttributeFormData: formData,
                cardAttributeQuery: queryConfig,
            });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.id,
                        resource: "CardAttribute",
                    }),
                );
            }
        },
        [formData, saveFormData, queryConfig, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardAttributeEntity>
                    name="CardAttributeEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
